import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import UserSelect from '@sayyesschool/frontend/components/user-select';
import { useManagers } from '@sayyesschool/frontend/features/managers';
import { requestStatusOptions } from '@sayyesschool/frontend/features/requests';
import { useSetting } from '@sayyesschool/frontend/features/settings';
import { useFormData } from '@sayyesschool/frontend/hooks/form';
import { Flex, Form, Text } from '@sayyesschool/frontend/ui-components';

import { getOptions } from './helpers';

const defaultRequest = {
    status: 'new',
    type: '',
    channel: '',
    source: '',
    contact: {},
    utm: {},
    note: ''
};

function RequestForm({
    request = {},
    onSubmit,
    onChange,
    ...props
}, ref) {
    const [managers] = useManagers();
    const [requestTypes = {}] = useSetting('request.types');
    const [requestChannels = {}] = useSetting('request.channels');
    const [requestSources = {}] = useSetting('request.sources');

    const formRef = useRef();

    const { data, handleChange } = useFormData({
        ...defaultRequest,
        ...request,
        learnerId: request?.learnerId,
        managerId: request?.managerId
    }, [request?.updatedAt]);

    useImperativeHandle(ref, () => ({
        get form() { return formRef.current; },
        get data() { return data; }
    }));

    useEffect(() => {
        data.contact.phone = data.contact.phone
            ? data.contact?.phone.replace(/ /gm, '')
            : undefined;

        onChange?.(data);
    }, [data, onChange]);

    const handleSubmit = useCallback(() => {
        data.contact.phone = data.contact.phone
            ? data.contact?.phone.replace(/ /gm, '')
            : undefined;

        onSubmit(data);
    }, [data, onSubmit]);

    const requestTypeOptions = getOptions(requestTypes);
    const requestChannelOptions = getOptions(requestChannels);
    const requestSourceOptions = getOptions(requestSources);

    return (
        <Form
            ref={formRef}
            className="Form"
            onSubmit={handleSubmit}
            {...props}
        >
            <Form.Select
                label="Статус"
                name="status"
                value={data.status}
                options={requestStatusOptions}
                required
                onChange={handleChange}
            />

            <Form.Input
                label="Имя"
                name="contact.name"
                value={data.contact?.name}
                required={!data.learnerId}
                onChange={handleChange}
            />

            <Form.Input
                label="Телефон"
                name="contact.phone"
                value={data.contact?.phone}
                required={!data.learnerId && !data?.contact?.email}
                onChange={handleChange}
            />

            <Form.Input
                label="Эл. почта"
                name="contact.email"
                value={data.contact?.email}
                required={!data.learnerId && !data?.contact?.phone}
                onChange={handleChange}
            />

            <Form.Select
                label="Тип"
                name="type"
                value={data.type}
                options={requestTypeOptions}
                onChange={handleChange}
            />

            <Form.Select
                label="Канал связи"
                name="channel"
                value={data.channel}
                options={requestChannelOptions}
                onChange={handleChange}
            />

            <Form.Select
                label="Источник"
                name="source"
                value={data.source}
                options={requestSourceOptions}
                onChange={handleChange}
            />

            {data.learnerId &&
                <UserSelect
                    label="Ученик"
                    name="learnerId"
                    value={data.learnerId}
                    options={[{
                        key: request.learner.id,
                        value: request.learner.id,
                        label: request.learner.fullname
                    }]}
                    readOnly={data.id}
                    disabled
                />
            }

            <UserSelect
                label="Менеджер"
                name="managerId"
                value={data.managerId}
                options={managers?.map(manager => ({
                    key: manager.id,
                    value: manager.id,
                    label: manager.fullname
                }))}
                required
                onChange={handleChange}
            />

            <Form.Textarea
                name="note"
                value={data.note}
                label="Примечание"
                onChange={handleChange}
            />

            <Flex as="fieldset" column>
                <Text as="legend">UTM</Text>

                <Form.Input
                    name="utm.source"
                    value={data.utm.source}
                    label="Source"
                    onChange={handleChange}
                />

                <Form.Input
                    name="utm.medium"
                    value={data.utm.medium}
                    label="Medium"
                    onChange={handleChange}
                />

                <Form.Input
                    name="utm.campaign"
                    value={data.utm.campaign}
                    label="Campaign"
                    onChange={handleChange}
                />

                <Form.Input
                    name="utm.term"
                    value={data.utm.term}
                    label="Term"
                    onChange={handleChange}
                />

                <Form.Input
                    name="utm.content"
                    value={data.utm.content}
                    label="Content"
                    onChange={handleChange}
                />
            </Flex>
        </Form>
    );
}

export default forwardRef(RequestForm);